// ./src/state/auth.ts
import { create } from 'zustand';
import {
  AuthService,
  type PlayerInfoResponse,
  extractNicknameFromPayload,
  PLAYER_NICKNAME_PATTERN,
  // PlayerSessionResponse,
} from '@core/Web/services/AuthService';
import { syncActiveGameBalance } from '@core/gameengine/state/activeGameStore';
import {
  getAuthToken as getAuthToken,
  getAuthTokenFromUrl,
  cleanAuthTokenFromUrl as cleanTokenFromUrl,
  getRefreshToken,
  getRefreshTokenExpiry,
  setRefreshToken as persistRefreshToken,
  setRefreshTokenExpiry as persistRefreshExpiry,
  cleanTokensFromUrl,
} from '@core/utils/authToken';
import { RefreshTokenService } from '@Web/services/RefreshTokenService';
import { removeRefreshToken } from '@core/utils/authToken';
import { clearGameSession, getPlayerSessionId } from '@core/utils/gameSession';
import { getGameFromUrl } from '@core/utils/urlParser';
import { formatSkyxPlayerDisplayName } from '@core/utils/skyxPlayerDisplayName';
import {
  identifyAnalyticsUser,
  resetAnalyticsUser,
  track,
} from '@core/analytics';

export type AuthUser = {
  playerId: string;
  currency: string;
  /** Menu / chat label for most games (display name when the API provides one). */
  username: string;
  /** Login / operator account id for session logs (never the friendly display name). */
  loginUsername: string;
  /** Server nickname (e.g. Player-a34f69) — used by sky-x new menu header. */
  nickname: string;
};

interface AuthState {
  accessToken: string | null;
  playerSessionId: string | null;
  user: AuthUser | null;
  isLoading: boolean;
  error: string | null;
  isDemo: boolean;
  isReady: boolean;
  isRestoring: boolean;
  login: (username: string, password: string) => Promise<void>;
  startDemo: () => Promise<void>;
  logout: () => void;
  restoreSession: () => void;
  initializeFromToken: (token: string) => Promise<void>;
}

const TOKEN_KEY = 'playgrid-access-token';
const EXPIRY_KEY = 'playgrid-token-expiry';

/**
 * Normalize various expiry representations to an ISO string.
 *
 * Accepts:
 *  - ISO string (returned as-is)
 *  - relative seconds (number) -> converts to an ISO (Date.now() + seconds*1000)
 *  - absolute ms timestamp (number > 1e12 or clearly ms) -> convert to ISO
 *  - null/undefined -> returns null
 */
function normalizeExpiryToISO(
  expiry: string | number | null | undefined
): string | null {
  if (!expiry && expiry !== 0) return null;

  // If it's a string that's already ISO, try parsing
  if (typeof expiry === 'string') {
    // If it looks like a number string, convert to number first
    const maybeNum = Number(expiry);
    if (!Number.isNaN(maybeNum)) {
      // treat as numeric timestamp or relative seconds? assume numeric string is ms timestamp if large
      // If it's very small (<1e11), assume seconds
      if (maybeNum < 1e11) {
        // seconds
        return new Date(Date.now() + maybeNum * 1000).toISOString();
      } else {
        // ms timestamp
        return new Date(maybeNum).toISOString();
      }
    }

    // otherwise assume ISO string already
    const parsed = Date.parse(expiry);
    if (!Number.isNaN(parsed)) {
      return new Date(parsed).toISOString();
    }
    return null;
  }

  if (typeof expiry === 'number') {
    // Heuristics:
    // - If number < 1e11 -> treat as seconds (relative)
    // - If number >= 1e11 -> treat as ms timestamp (absolute)
    if (expiry < 1e11) {
      // seconds relative
      return new Date(Date.now() + expiry * 1000).toISOString();
    } else {
      // absolute ms timestamp
      return new Date(expiry).toISOString();
    }
  }

  return null;
}

/**
 * Schedule a callback to run exactly when expiry occurs (server-controlled).
 *
 * Accepts expiry in ISO or numeric forms (same normalization above).
 */
function scheduleAtExpiry(
  expiryValue: string | number | null | undefined,
  callback: () => void
) {
  const expiryISO = normalizeExpiryToISO(expiryValue);
  if (!expiryISO) {
    console.warn(
      '[Auth] scheduleAtExpiry called with invalid expiry:',
      expiryValue
    );
    return;
  }

  const expiryTime = new Date(expiryISO).getTime();
  const now = Date.now();
  const delay = expiryTime - now;

  console.log('[Auth] scheduleAtExpiry - expiryISO:', expiryISO);
  console.log('[Auth] scheduleAtExpiry - scheduling in (ms):', delay);

  if (delay <= 0) {
    console.log(
      '[Auth] scheduleAtExpiry - expiry already passed or immediate -> executing callback now'
    );
    try {
      callback();
    } catch (e) {
      console.error('[Auth] scheduleAtExpiry - callback error', e);
    }
    return;
  }

  setTimeout(async () => {
    try {
      await callback();
    } catch (e) {
      console.error('[Auth] scheduleAtExpiry - callback threw', e);
    }
  }, delay);
}

const NICKNAME_STORAGE_KEY = 'playgrid-player-nickname';
/** Game-table nickname (Player-xxxx) — separate from lobby display name. */
const GAME_NICKNAME_STORAGE_KEY = 'playgrid-player-game-nickname';
/** Login form username — stable sky-x menu fallback before JOIN assigns Player-xxxx. */
const LOGIN_USERNAME_STORAGE_KEY = 'playgrid-login-username';

/** Legacy placeholder values — never show these in the menu. */
const PLACEHOLDER_USERNAMES = new Set([
  'lobby-user',
  'restored-user',
  'demo-user',
]);

function persistGameNickname(nickname: string): void {
  const trimmed = nickname.trim();
  if (!trimmed || PLACEHOLDER_USERNAMES.has(trimmed.toLowerCase())) {
    return;
  }
  try {
    localStorage.setItem(GAME_NICKNAME_STORAGE_KEY, trimmed);
  } catch {
    // ignore
  }
}

export function readPersistedGameNickname(): string {
  try {
    const stored =
      localStorage.getItem(GAME_NICKNAME_STORAGE_KEY)?.trim() || '';
    if (stored && !PLACEHOLDER_USERNAMES.has(stored.toLowerCase())) {
      return stored;
    }
  } catch {
    // ignore
  }
  return '';
}

export function persistLoginUsername(username: string): void {
  const trimmed = username.trim();
  if (!trimmed || PLACEHOLDER_USERNAMES.has(trimmed.toLowerCase())) {
    return;
  }
  try {
    localStorage.setItem(LOGIN_USERNAME_STORAGE_KEY, trimmed);
  } catch {
    // ignore
  }
}

export function readPersistedLoginUsername(): string {
  try {
    const stored =
      localStorage.getItem(LOGIN_USERNAME_STORAGE_KEY)?.trim() || '';
    if (stored && !PLACEHOLDER_USERNAMES.has(stored.toLowerCase())) {
      return stored;
    }
  } catch {
    // ignore
  }
  return '';
}

function isDisplayablePlayerLabel(
  value: string | undefined | null
): value is string {
  const trimmed = value?.trim() ?? '';
  return (
    trimmed.length > 0 && !PLACEHOLDER_USERNAMES.has(trimmed.toLowerCase())
  );
}

function isSkyxTableNickname(value: string): boolean {
  return PLAYER_NICKNAME_PATTERN.test(value.trim());
}

function isLikelyEmail(value: string): boolean {
  return value.includes('@');
}

/** Ignore old display-name values that were mistakenly persisted as game nicknames. */
function isStaleLobbyDisplayName(
  value: string,
  user: AuthUser | null
): boolean {
  const trimmed = value.trim();
  const display = user?.username?.trim() ?? '';
  return (
    Boolean(display) && trimmed === display && !isSkyxTableNickname(trimmed)
  );
}

/** Login username or custom nickname — should not be replaced by auto Player-xxxx. */
function hasStableSkyxMenuIdentity(user: AuthUser | null): boolean {
  const serverNick = user?.nickname?.trim() ?? '';
  if (
    isDisplayablePlayerLabel(serverNick) &&
    !isSkyxTableNickname(serverNick)
  ) {
    return true;
  }
  if (readPersistedLoginUsername()) {
    return true;
  }
  const displayName = user?.username?.trim() ?? '';
  return (
    isDisplayablePlayerLabel(displayName) &&
    !isLikelyEmail(displayName) &&
    !PLACEHOLDER_USERNAMES.has(displayName.toLowerCase())
  );
}

/**
 * sky-x new menu header: keep login/display identity stable; use table Player-xxxx
 * only when no better label exists.
 */
export function resolveSkyxPlayerMenuLabel(
  user: AuthUser | null,
  liveBetNickname = ''
): string {
  const serverNick = user?.nickname?.trim() ?? '';
  if (
    isDisplayablePlayerLabel(serverNick) &&
    !isSkyxTableNickname(serverNick)
  ) {
    return formatSkyxPlayerDisplayName(serverNick);
  }

  const loginUsername = readPersistedLoginUsername();
  if (
    isDisplayablePlayerLabel(loginUsername) &&
    !isStaleLobbyDisplayName(loginUsername, user)
  ) {
    return formatSkyxPlayerDisplayName(loginUsername);
  }

  const displayName = user?.username?.trim() ?? '';
  if (
    isDisplayablePlayerLabel(displayName) &&
    !isLikelyEmail(displayName) &&
    !PLACEHOLDER_USERNAMES.has(displayName.toLowerCase())
  ) {
    return formatSkyxPlayerDisplayName(displayName);
  }

  if (isDisplayablePlayerLabel(serverNick)) {
    return formatSkyxPlayerDisplayName(serverNick);
  }

  const liveNick = liveBetNickname.trim();
  if (isDisplayablePlayerLabel(liveNick)) {
    return formatSkyxPlayerDisplayName(liveNick);
  }

  const gameNick = readPersistedGameNickname();
  if (
    isDisplayablePlayerLabel(gameNick) &&
    !isStaleLobbyDisplayName(gameNick, user)
  ) {
    return formatSkyxPlayerDisplayName(gameNick);
  }

  return '';
}

/** Update in-game nickname (sky-x menu / scoreboard label) without touching display name. */
export function patchAuthUserNickname(nickname: string): void {
  const trimmed = nickname.trim();
  if (!trimmed || PLACEHOLDER_USERNAMES.has(trimmed.toLowerCase())) {
    return;
  }
  persistGameNickname(trimmed);
  const current = useAuth.getState().user;
  if (!current) {
    return;
  }
  // Table-assigned Player-xxxx is for scoreboard — do not overwrite login/display name.
  if (isSkyxTableNickname(trimmed) && hasStableSkyxMenuIdentity(current)) {
    return;
  }
  if (current.nickname === trimmed) {
    return;
  }
  useAuth.setState({
    user: { ...current, nickname: trimmed },
  });
}

/** Apply a nickname parsed from any server payload (JOIN_ACK, profile, STOMP). */
export function applyServerNicknameFromPayload(payload: unknown): boolean {
  const nickname = extractNicknameFromPayload(payload).trim();
  if (!nickname) {
    return false;
  }
  patchAuthUserNickname(nickname);
  return true;
}

function persistPlayerNickname(nickname: string): void {
  const trimmed = nickname.trim();
  if (!trimmed || PLACEHOLDER_USERNAMES.has(trimmed.toLowerCase())) {
    return;
  }
  try {
    localStorage.setItem(NICKNAME_STORAGE_KEY, trimmed);
  } catch {
    // ignore
  }
}

function readPersistedPlayerNickname(): string {
  try {
    const stored = localStorage.getItem(NICKNAME_STORAGE_KEY)?.trim() || '';
    if (stored && !PLACEHOLDER_USERNAMES.has(stored.toLowerCase())) {
      return stored;
    }
  } catch {
    // ignore
  }
  return '';
}

function buildAuthUserFromProfile(
  profile: PlayerInfoResponse,
  playerId: string,
  currency: string,
  loginUsernameFallback = ''
): AuthUser {
  let serverNickname = profile.nickname.trim() || readPersistedGameNickname();
  const profileDisplayName = profile.displayName.trim();
  if (!serverNickname && isSkyxTableNickname(profileDisplayName)) {
    serverNickname = profileDisplayName;
  }
  const displayName =
    profileDisplayName ||
    loginUsernameFallback.trim() ||
    readPersistedPlayerNickname();
  const accountUsername =
    profile.username.trim() ||
    loginUsernameFallback.trim() ||
    readPersistedLoginUsername();
  if (serverNickname) {
    persistGameNickname(serverNickname);
  }
  if (displayName) {
    persistPlayerNickname(displayName);
  }
  if (accountUsername) {
    persistLoginUsername(accountUsername);
  }
  const loginUsername = accountUsername;
  return {
    playerId,
    currency,
    username: displayName || serverNickname || loginUsername,
    loginUsername,
    nickname: serverNickname,
  };
}

async function hydrateUserFromAccessToken(
  accessToken: string
): Promise<AuthUser> {
  const response = await AuthService.getInstance().getPlayerInfo(accessToken);
  const currency = response.wallet?.currency || 'EUR';
  const balance = response.wallet?.balance ?? 0;

  syncActiveGameBalance(balance, currency);
  AuthService.getInstance().setAccessToken(accessToken);

  return buildAuthUserFromProfile(response, response.id, currency);
}

export const useAuth = create<AuthState>(set => ({
  accessToken: null,
  playerSessionId: null,
  user: null,
  isLoading: false,
  error: null,
  isDemo: false,
  isReady: false,
  isRestoring: false,

  login: async (username, password) => {
    set({ isLoading: true, error: null, isReady: false, isDemo: false });
    clearGameSession();
    persistLoginUsername(username);
    try {
      // Device detection remains simple and client-side
      const getDeviceType = (): string => {
        if (typeof window === 'undefined') return 'desktop';
        if (window.innerWidth >= 1024) return 'desktop';
        if (window.innerWidth >= 620) return 'tablet';
        return 'mobile';
      };

      const deviceType = getDeviceType();
      const ipAddress = '';

      const sessionResponse = await AuthService.getInstance().login(
        username,
        password,
        deviceType,
        ipAddress
      );
      const accessToken = AuthService.getInstance().getAccessToken();

      // If AuthService set a refresh token during login, persist it (and normalize expiry)
      const persistedRefresh = AuthService.getInstance().getRefreshToken();
      const persistedRefreshExpiryRaw =
        AuthService.getInstance().getRefreshTokenExpiry();

      if (persistedRefresh) {
        try {
          persistRefreshToken(persistedRefresh, undefined);
        } catch {
          // ignore
        }
      }

      if (persistedRefreshExpiryRaw) {
        const normalized = normalizeExpiryToISO(persistedRefreshExpiryRaw);
        if (normalized) {
          try {
            persistRefreshExpiry(normalized);
          } catch {
            // ignore
          }
        }
      }

      if (
        !accessToken ||
        !sessionResponse ||
        !sessionResponse.playerSessionId ||
        !sessionResponse.wallets
      ) {
        throw new Error('Invalid session response from server.');
      }

      const expiresInMs = 24 * 60 * 60 * 1000; // still store access token expiry locally as 24h for client-side checks
      const expiryTimestamp = Date.now() + expiresInMs;
      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(EXPIRY_KEY, expiryTimestamp.toString());
      const preferredCurrency =
        sessionResponse.wallets.find(w => w.currency === 'EUR')?.currency ||
        sessionResponse.wallets[0]?.currency;

      const rawBalance =
        sessionResponse.wallets.find(w => w.currency === 'EUR')
          ?.cashAvailable ?? sessionResponse.wallets[0]?.cashAvailable;

      if (!preferredCurrency || rawBalance === undefined) {
        throw new Error(
          'No valid currency or balance found in session response.'
        );
      }

      const balance = parseFloat((rawBalance / 100).toFixed(2));

      syncActiveGameBalance(balance, preferredCurrency);

      let authUser: AuthUser;
      try {
        const profile =
          await AuthService.getInstance().getPlayerInfo(accessToken);
        authUser = buildAuthUserFromProfile(
          profile,
          sessionResponse.playerId,
          preferredCurrency,
          username
        );
      } catch (profileError) {
        console.warn(
          '[Auth] Could not load player nickname after login:',
          profileError
        );
        const fallbackName = username.trim() || readPersistedPlayerNickname();
        if (fallbackName) {
          persistPlayerNickname(fallbackName);
        }
        authUser = {
          playerId: sessionResponse.playerId,
          currency: preferredCurrency,
          username: fallbackName,
          loginUsername: username.trim() || readPersistedLoginUsername(),
          nickname: readPersistedGameNickname(),
        };
      }

      set({
        accessToken: accessToken,
        playerSessionId: sessionResponse.playerSessionId,
        user: authUser,
        isLoading: false,
        error: null,
        isReady: true,
      });

      // If server provided refresh expiry info as relative seconds in the sessionResponse, handle it
      // (Many servers return refresh_expires_in or similar in seconds)
      if ((sessionResponse as any).refresh_expires_in) {
        const secs = (sessionResponse as any).refresh_expires_in;
        const normalized = normalizeExpiryToISO(secs);
        if (normalized) {
          try {
            persistRefreshExpiry(normalized);
            AuthService.getInstance().setRefreshTokenExpiry(normalized);
            // schedule refresh exactly at server expiry
            scheduleAtExpiry(normalized, async () => {
              const result =
                await RefreshTokenService.getInstance().refreshToken();
              if (result) {
                // If server returns refresh_expires_in, convert & persist then reschedule
                const newExpiryVal =
                  (result as any).refresh_expires_in ??
                  (result as any).expiresAt ??
                  AuthService.getInstance().getRefreshTokenExpiry();
                const normalizedNew = normalizeExpiryToISO(newExpiryVal);
                if (normalizedNew) {
                  AuthService.getInstance().setRefreshTokenExpiry(
                    normalizedNew
                  );
                  try {
                    persistRefreshExpiry(normalizedNew);
                  } catch {
                    // ignore
                  }
                  // reschedule next refresh
                  scheduleAtExpiry(normalizedNew, () => {
                    RefreshTokenService.getInstance().refreshToken();
                  });
                }
              }
            });
          } catch {
            // ignore
          }
        }
      }

      identifyAnalyticsUser({
        userId: authUser.playerId,
        isDemo: false,
      });
      track('login_success', { method: 'password' });
    } catch (err: any) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(EXPIRY_KEY);
      track('login_failure', {
        reason: String(err?.message || 'unknown').slice(0, 80),
      });
      set({
        error: err.message || 'An unexpected error occurred during login.',
        isLoading: false,
        accessToken: null,
        playerSessionId: null,
        user: null,
        isReady: true,
      });
    }
  },

  startDemo: async () => {
    set({ isLoading: true, error: null, isDemo: true, isReady: false });
    try {
      const demoResponse = await AuthService.getInstance().startDemoSession();

      if (
        !demoResponse ||
        !demoResponse.access_token ||
        !demoResponse.playerId ||
        demoResponse.chips === undefined
      ) {
        throw new Error('Invalid demo response from server.');
      }
      const demoCurrency = 'DEMO';
      const demoBalance = parseFloat((demoResponse.chips / 100).toFixed(2));

      // Persist refresh token if provided by AuthService
      const persistedRefresh = AuthService.getInstance().getRefreshToken();
      const persistedRefreshExpiryRaw =
        AuthService.getInstance().getRefreshTokenExpiry();

      if (persistedRefresh) {
        try {
          persistRefreshToken(persistedRefresh, undefined);
        } catch {
          // ignore
        }
      }

      if (persistedRefreshExpiryRaw) {
        const normalized = normalizeExpiryToISO(persistedRefreshExpiryRaw);
        if (normalized) {
          try {
            persistRefreshExpiry(normalized);
          } catch {
            // ignore
          }
        }
      }

      syncActiveGameBalance(demoBalance, demoCurrency);

      AuthService.getInstance().setAccessToken(demoResponse.access_token);
      const demoExpiryTimestamp = Date.now() + 24 * 60 * 60 * 1000;
      localStorage.setItem(TOKEN_KEY, demoResponse.access_token);
      localStorage.setItem(EXPIRY_KEY, demoExpiryTimestamp.toString());

      let demoUser: AuthUser = {
        playerId: demoResponse.playerId,
        currency: demoCurrency,
        username: 'demo-user',
        loginUsername: 'demo-user',
        nickname: '',
      };
      try {
        const profile = await AuthService.getInstance().getPlayerInfo(
          demoResponse.access_token
        );
        demoUser = buildAuthUserFromProfile(
          profile,
          demoResponse.playerId,
          demoCurrency
        );
      } catch {
        // Demo menu may omit nickname until profile is available.
      }

      set({
        accessToken: demoResponse.access_token,
        playerSessionId: null,
        user: demoUser,
        isLoading: false,
        error: null,
        isReady: true,
        isDemo: true,
      });

      // If demoResponse includes refresh_expires_in schedule based on server value
      if ((demoResponse as any).refresh_expires_in) {
        const normalized = normalizeExpiryToISO(
          (demoResponse as any).refresh_expires_in
        );
        if (normalized) {
          try {
            persistRefreshExpiry(normalized);
            AuthService.getInstance().setRefreshTokenExpiry(normalized);
            scheduleAtExpiry(normalized, async () => {
              const result =
                await RefreshTokenService.getInstance().refreshToken();
              if (result) {
                const newExpiryVal =
                  (result as any).refresh_expires_in ??
                  (result as any).expiresAt ??
                  AuthService.getInstance().getRefreshTokenExpiry();
                const normalizedNew = normalizeExpiryToISO(newExpiryVal);
                if (normalizedNew) {
                  AuthService.getInstance().setRefreshTokenExpiry(
                    normalizedNew
                  );
                  try {
                    persistRefreshExpiry(normalizedNew);
                  } catch {
                    // ignore
                  }
                  scheduleAtExpiry(normalizedNew, () => {
                    RefreshTokenService.getInstance().refreshToken();
                  });
                }
              }
            });
          } catch {
            // ignore
          }
        }
      }

      identifyAnalyticsUser({
        userId: demoUser.playerId,
        isDemo: true,
      });
      track('demo_success');
    } catch (err: any) {
      track('demo_failure', {
        reason: String(err?.message || 'unknown').slice(0, 80),
      });
      set({
        error: err.message || 'An unexpected error occurred during demo start.',
        isLoading: false,
        accessToken: null,
        playerSessionId: null,
        user: null,
        isReady: true,
        isDemo: false,
      });
    }
  },
  /**
   * NEW: The "Direct URL" flow.
   * Fetches data via GET and sets up the session without traditional login.
   */
  initializeFromToken: async (token: string) => {
    set({ isLoading: true, isReady: false, isRestoring: true });
    clearGameSession();

    try {
      const profile = await hydrateUserFromAccessToken(token);

      // Persistence for session recovery later
      const expiresInMs = 24 * 60 * 60 * 1000;
      const expiryTimestamp = Date.now() + expiresInMs;
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(EXPIRY_KEY, expiryTimestamp.toString());

      cleanTokenFromUrl();

      identifyAnalyticsUser({
        userId: profile.playerId,
        isDemo: false,
      });
      set({
        accessToken: token,
        playerSessionId: null,
        user: profile,
        isLoading: false,
        isReady: true,
        isRestoring: false,
        error: null,
      });

      console.log('[Auth] Direct session initialized for:');
    } catch (err: any) {
      console.error('[Auth] Direct Token initialization failed:', err);
      set({
        error: 'Failed to initialize player session.',
        isLoading: false,
        isReady: true,
        isRestoring: false,
      });
    }
  },

  restoreSession: async () => {
    const currentState = useAuth.getState();
    if (currentState.isRestoring) {
      return;
    }

    const urlToken = getAuthTokenFromUrl();
    const { pathname } = window.location;

    const gameSlug = (getGameFromUrl() ?? '').toLowerCase();
    const isRemoteLaunch =
      pathname.startsWith('/client/crash') ||
      gameSlug === 'skyx' ||
      gameSlug === 'skyx_new' ||
      gameSlug === 'sky-x';
    if (urlToken && isRemoteLaunch) {
      console.log(
        '[Auth] URL Token detected. Running parallel initialization...'
      );
      return useAuth.getState().initializeFromToken(urlToken);
    }

    set({ isLoading: true, isReady: false, error: null, isRestoring: true });
    clearGameSession();

    // Priority: 1. URL token, 2. cookie, 3. localStorage
    const externalToken = getAuthToken();
    const isTokenFromExternal = !!externalToken;
    let accessToken = externalToken;

    const lobbyRefreshToken = getRefreshToken();
    const lobbyRefreshTokenExpiryRaw = getRefreshTokenExpiry();

    console.log(
      '[Auth] Restoring session. lobbyRefreshTokenExpiry from lobby:',
      lobbyRefreshTokenExpiryRaw
    );
    console.log(
      '[Auth] Restoring session. refreshToken from lobby:',
      lobbyRefreshToken
    );

    // If a refresh token came in via lobby, ensure AuthService has it and persist it
    if (lobbyRefreshToken) {
      AuthService.getInstance().setRefreshToken(
        lobbyRefreshToken,
        // if lobbyRefreshTokenExpiryRaw is seconds or ISO this setter should handle numeric or string; but to be safe we convert below
        undefined
      );

      // Normalize and persist expiry if present
      if (lobbyRefreshTokenExpiryRaw) {
        const normalized = normalizeExpiryToISO(lobbyRefreshTokenExpiryRaw);
        if (normalized) {
          AuthService.getInstance().setRefreshTokenExpiry(normalized);
          try {
            persistRefreshToken(lobbyRefreshToken, undefined);
            persistRefreshExpiry(normalized);
          } catch {
            // ignore
          }

          // schedule refresh exactly at server expiry (no 2-minute offset)
          scheduleAtExpiry(normalized, async () => {
            console.log(
              '[Auth] Running refresh at server-controlled expiry (lobby token)'
            );
            const result =
              await RefreshTokenService.getInstance().refreshToken();
            if (result) {
              const newExpiryVal =
                (result as any).refresh_expires_in ??
                (result as any).expiresAt ??
                AuthService.getInstance().getRefreshTokenExpiry();
              const normalizedNew = normalizeExpiryToISO(newExpiryVal);
              if (normalizedNew) {
                AuthService.getInstance().setRefreshTokenExpiry(normalizedNew);
                try {
                  persistRefreshExpiry(normalizedNew);
                } catch {
                  // ignore
                }
                scheduleAtExpiry(normalizedNew, () => {
                  RefreshTokenService.getInstance().refreshToken();
                });
              }
            } else {
              console.warn(
                '[Auth] Failed to refresh token (lobby flow) - user may need to re-login'
              );
            }
          });
        }
      }
    }

    // Clean token params from URL if present
    if (externalToken) {
      cleanTokenFromUrl();
    }
    // Also remove any token-bearing params (refreshToken etc.)
    cleanTokensFromUrl();

    // If no token from URL/cookie, check localStorage directly
    if (!accessToken) {
      accessToken = localStorage.getItem(TOKEN_KEY);
    }

    const expiry = localStorage.getItem(EXPIRY_KEY);
    const now = Date.now();

    if (accessToken) {
      // If token came from localStorage, check expiry first
      const isValidExpiry = expiry && parseInt(expiry, 10) > now;

      // If token is from localStorage and expired, skip
      if (!isTokenFromExternal && !isValidExpiry) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(EXPIRY_KEY);
        set({ isLoading: false, isReady: true, isRestoring: false });
        return;
      }

      try {
        if (isTokenFromExternal) {
          console.log(
            '[Auth] Token from cookie/lobby - loading player profile for nickname'
          );

          const profile = await hydrateUserFromAccessToken(accessToken);

          const expiresInMs = 24 * 60 * 60 * 1000;
          const expiryTimestamp = Date.now() + expiresInMs;
          localStorage.setItem(TOKEN_KEY, accessToken);
          localStorage.setItem(EXPIRY_KEY, expiryTimestamp.toString());

          identifyAnalyticsUser({
            userId: profile.playerId,
            isDemo: false,
          });
          set({
            accessToken: accessToken,
            playerSessionId: getPlayerSessionId(),
            user: profile,
            isLoading: false,
            error: null,
            isReady: true,
            isDemo: false,
            isRestoring: false,
          });
        } else {
          // Token from localStorage - use the old flow with startPlayerSession API
          const sessionResponse =
            await AuthService.getInstance().startPlayerSession(accessToken);

          const preferredCurrency =
            sessionResponse.wallets.find(w => w.currency === 'EUR')?.currency ||
            sessionResponse.wallets[0]?.currency;

          const rawBalance =
            sessionResponse.wallets.find(w => w.currency === 'EUR')
              ?.cashAvailable ?? sessionResponse.wallets[0]?.cashAvailable;

          if (!preferredCurrency || rawBalance === undefined) {
            throw new Error(
              'No valid currency or balance found in restored session.'
            );
          }

          const balance = parseFloat((rawBalance / 100).toFixed(2));

          syncActiveGameBalance(balance, preferredCurrency);

          // If startPlayerSession returned a refresh token, persist and schedule refresh
          const currentRefresh = AuthService.getInstance().getRefreshToken();
          const currentRefreshExpiryRaw =
            AuthService.getInstance().getRefreshTokenExpiry();

          if (currentRefresh) {
            try {
              persistRefreshToken(currentRefresh, undefined);
            } catch {
              // ignore
            }
          }

          if (currentRefreshExpiryRaw) {
            const normalized = normalizeExpiryToISO(currentRefreshExpiryRaw);
            if (normalized) {
              try {
                persistRefreshExpiry(normalized);
              } catch {
                // ignore
              }

              // schedule based on persisted expiry (server-controlled)
              scheduleAtExpiry(normalized, async () => {
                console.log(
                  '[Auth] Running refresh at server-controlled expiry (restored session)'
                );
                const result =
                  await RefreshTokenService.getInstance().refreshToken();
                if (result) {
                  const newExpiryVal =
                    (result as any).refresh_expires_in ??
                    (result as any).expiresAt ??
                    AuthService.getInstance().getRefreshTokenExpiry();
                  const normalizedNew = normalizeExpiryToISO(newExpiryVal);
                  if (normalizedNew) {
                    AuthService.getInstance().setRefreshTokenExpiry(
                      normalizedNew
                    );
                    try {
                      persistRefreshExpiry(normalizedNew);
                    } catch {
                      // ignore
                    }
                    scheduleAtExpiry(normalizedNew, () => {
                      RefreshTokenService.getInstance().refreshToken();
                    });
                  }
                } else {
                  console.warn(
                    '[Auth] Failed to refresh token (restored flow) - user may need to re-login'
                  );
                }
              });
            }
          }

          let authUser: AuthUser;
          try {
            const profile =
              await AuthService.getInstance().getPlayerInfo(accessToken);
            authUser = buildAuthUserFromProfile(
              profile,
              sessionResponse.playerId,
              preferredCurrency
            );
          } catch (profileError) {
            console.warn(
              '[Auth] Could not load player nickname on restore:',
              profileError
            );
            const fallbackName =
              readPersistedPlayerNickname() || sessionResponse.playerId;
            authUser = {
              playerId: sessionResponse.playerId,
              currency: preferredCurrency,
              username: fallbackName,
              loginUsername: readPersistedLoginUsername(),
              nickname: readPersistedGameNickname(),
            };
          }

          identifyAnalyticsUser({
            userId: authUser.playerId,
            isDemo: false,
          });
          set({
            accessToken: accessToken,
            playerSessionId: sessionResponse.playerSessionId,
            user: authUser,
            isLoading: false,
            error: null,
            isReady: true,
            isDemo: false,
            isRestoring: false,
          });
        }
      } catch (_err: any) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(EXPIRY_KEY);
        set({
          accessToken: null,
          playerSessionId: null,
          user: null,
          error: 'Session expired. Please log in again.',
          isLoading: false,
          isReady: true,
          isRestoring: false,
        });
      }
    } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(EXPIRY_KEY);
      set({ isLoading: false, isReady: true, isRestoring: false });
    }
  },

  logout: () => {
    track('logout');
    resetAnalyticsUser();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRY_KEY);
    localStorage.removeItem(NICKNAME_STORAGE_KEY);

    try {
      removeRefreshToken();
      clearGameSession();
    } catch {
      // ignore
    }

    set({
      accessToken: null,
      playerSessionId: null,
      user: null,
      error: null,
      isLoading: false,
      isDemo: false,
      isReady: true,
      isRestoring: false,
    });
  },
}));

export { normalizeExpiryToISO, scheduleAtExpiry };
